import { useCallback, useMemo, useState } from 'react'
import { Alert, Button, Card, DatePicker, Drawer, Input, Select, Space, Spin, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { getTeacherPerformance, getTeacherPerformances } from '~/services/performance'
import { bonusRate, compareRates, compareScores, filterRecords, filterTeachers, formatPart, formatRate, formatTime,
  hasPendingData, metricKeys, pointLosses, type LossKey, type MetricKey, type PerformanceRecord, type RecordFilter,
  type ScorePartKey, type TeacherPerformance } from '~/features/performance/model'
import { useRemoteData } from '~/features/performance/useRemoteData'

const labels: Record<MetricKey, string> = { lateness: '课堂迟到率', absence: '老师缺席率', summaryCompletion: '总结填写率',
  summaryTimeliness: '总结按时提交率', cancellation: '老师取消率', momentSent: 'Moment 主动发送', momentReply: 'Moment 48 小时回复率' }
const help: Record<MetricKey, string> = {
  lateness: '迟到课数 ÷ 有可靠到课记录的非老师缺席课数；完全沿用现有迟到判定。',
  absence: '老师缺席课数 ÷ 已完成出勤判定课数。双方缺席计入，学生单独缺席不计为老师缺席。',
  summaryCompletion: '有效总结数 ÷ 应填写总结课数。有效总结须有学生评价、词汇，以及作业或重点句子之一。',
  summaryTimeliness: '课后 3 小时内首次有效提交数 ÷ 可判定的应提交课数。超时未提交计入分母；历史提交时间未知、仍在 3 小时内待提交的课程暂不计入。',
  cancellation: '老师取消课数 ÷（已完成出勤判定课数＋老师取消课数）。请假一律计入，学生取消和未接受的期望申请不计入。',
  momentSent: '每月按天分成 10 个时间段（约 3 天一段）。本月上过课（含老师缺席）的每个学生，从他第一节课那天之后开始的每一段，都需要至少 1 条老师主动发的 Moment；同一段发多条只算 1 次，段结束时没发就算错过，不能补。已完成的时间段 ÷ 已判定的时间段；进行中的一段发了才计入。系统每日朗读、已撤回的不算。',
  momentReply: '学生连续发的消息算一个回合，老师在 48 小时内回复即达标；达标回合 ÷ 已过回复期的回合。仍在 48 小时内的暂不计入。',
}
const scoreRule = '课堂 75 分：每节到期的课（含缺席、取消）从 1 分起扣，迟到 −0.3、总结超时 −0.2、总结未交 −0.5；老师取消记 0.5，老师缺席记 0。'
  + '缺席另按缺席率每 1% 扣 3 分（最多 40 分）。Moment 25 分：主动发送 15 分（每月 10 个时间段，每段每个学生至少 1 条，错过不能补）、48 小时回复 10 分，没有可发对象或无需回复时给满。'
  + '合计四舍五入一次，90–94 分奖金 5%，95–100 分奖金 10%。本月没有到期的课不评分。'
const lessonKeys: MetricKey[] = ['lateness', 'absence', 'cancellation', 'summaryCompletion', 'summaryTimeliness']
const momentKeys: MetricKey[] = ['momentSent', 'momentReply']
const partLabels: Record<ScorePartKey, string> = { lessons: '课堂（满分 75）', absence: '缺席扣分（最多 −40）',
  momentSent: 'Moment 主动发送（满分 15）', momentReply: 'Moment 48 小时回复（满分 10）' }
const lossLabels: Record<LossKey, (count: number) => string> = {
  absence: count => `老师缺席 ${count} 节`, lateness: count => `迟到 ${count} 节`,
  summaryCompletion: count => `总结未交 ${count} 节`, summaryTimeliness: count => `总结超时 ${count} 节`,
  cancellation: count => `老师取消 ${count} 节`, momentSent: count => `Moment 错过 ${count} 个时间段`,
  momentReply: count => `Moment 超时或未回复 ${count} 次`,
}

/** 总分配色与奖金档位一致：95+ 绿、90–94 蓝、其余不着色。 */
function ScoreTag({ score }: { score: number | null | undefined }) {
  if (score == null) return <Tooltip title="本月没有到期的课，不评分"><Typography.Text type="secondary">—</Typography.Text></Tooltip>
  return <Tag color={score >= 95 ? 'green' : score >= 90 ? 'blue' : 'default'} style={{ fontWeight: 600, fontSize: 14 }}>{score}</Tag>
}
function BonusTag({ report }: { report: TeacherPerformance }) {
  const rate = bonusRate(report.score?.value)
  return <Space size={4}>
    {rate ? <Tag color={rate === 10 ? 'green' : 'blue'}>+{rate}%</Tag> : <Typography.Text type="secondary">无</Typography.Text>}
    {hasPendingData(report) && <Tooltip title="还有出勤待判定、总结或 Moment 仍在期限内，分数和奖金可能变化"><Tag color="gold">预估</Tag></Tooltip>}
  </Space>
}

function ScoreSummary({ data }: { data: TeacherPerformance }) {
  const score = data.score?.value ?? null, rate = bonusRate(score), losses = pointLosses(data)
  const next = score == null || score >= 95 ? null : score >= 90 ? { points: 95 - score, rate: 10 } : { points: 90 - score, rate: 5 }
  return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
    <Card size="small" title="总分与奖金">
      <Space align="baseline" size="middle">
        <Typography.Title level={1} style={{ margin: 0 }}>{score ?? '—'}</Typography.Title>
        <Typography.Text type="secondary">/ 100</Typography.Text>
        <BonusTag report={data} />
      </Space>
      <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
        {score == null ? '本月没有到期的课，不评分，也没有奖金。'
          : (rate ? `奖金：课酬（扣除缺席后）的 ${rate}%。` : '未达到奖金档位。') + (next ? `再得 ${next.points} 分可到 ${next.rate}%。` : '已是最高档。')}
      </Typography.Paragraph>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>档位：90 分以下无 · 90–94 分 5% · 95–100 分 10%</Typography.Text>
    </Card>
    <Card size="small" title="分数构成">
      {data.score?.parts?.length ? data.score.parts.map(part => <div key={part.key}
        style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '3px 0' }}>
        <Typography.Text>{partLabels[part.key]}</Typography.Text>
        <Typography.Text strong type={part.key === 'absence' && Number(part.points) < 0 ? 'danger' : undefined}>
          {formatPart(part)}{part.excludedReason ? <Typography.Text type="secondary">（无需求，给满）</Typography.Text> : null}
        </Typography.Text>
      </div>) : <Typography.Text type="secondary">—</Typography.Text>}
    </Card>
    <Card size="small" title="主要扣分">
      {score == null ? <Typography.Text type="secondary">—</Typography.Text>
        : losses.length === 0 ? <Typography.Text type="success">本月没有扣分</Typography.Text>
          : losses.slice(0, 4).map(loss => <div key={loss.key}
            style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '3px 0' }}>
            <Typography.Text>{lossLabels[loss.key](loss.count)}</Typography.Text>
            <Typography.Text type="danger" strong>−{loss.points}</Typography.Text>
          </div>)}
    </Card>
  </div>
}

function MetricCards({ data, keys }: { data: TeacherPerformance; keys: MetricKey[] }) {
  return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
    {keys.map(key => <Card key={key} size="small" title={labels[key]}>
      <Typography.Title level={3}>{formatRate(data[key], 'zh-CN')}</Typography.Title>
      <Typography.Text type="secondary">{data[key].numerator} / {data[key].denominator}{key === 'momentSent' ? ` · 共发送 ${data.momentSentCount} 条` : ''}</Typography.Text>
      <Typography.Paragraph type="secondary">{help[key]}</Typography.Paragraph>
    </Card>)}
  </div>
}

const outcomes: Record<string, string> = { COMPLETED: '课程完成', STUDENT_ABSENT: '学生缺席', TEACHER_ABSENT: '老师缺席', TEACHER_CANCELLED: '老师取消' }
const timing: Record<string, string> = { ON_TIME: '按时提交', LATE: '超时提交', MISSING: '逾期未提交', PENDING: '仍在提交期限内', UNKNOWN: '提交时间未知', NOT_REQUIRED: '无需填写' }

function Coverage({ data }: { data: TeacherPerformance }) {
  return <Alert type="info" showIcon message={`到课时间未知 ${data.unknownLatenessCount} 节 · 总结提交时间未知 ${data.unknownSummaryTimingCount} 节 · 期限内待提交 ${data.pendingSummaryCount} 节 · Moment 回复期内 ${data.pendingReplyCount} 个 · 出勤待判定 ${data.unsettledCount} 节`}
    description={`按老师时区 ${data.timeZone} 的原定上课月份统计，未来课程暂不纳入。未知数据仅从对应指标分母中排除，不按准时或超时处理。月中的分数只评已经发生的事情，月底确定。`
      + (data.momentPendingStudents ? `当前 Moment 时间段还有 ${data.momentPendingStudents} 个学生没发（${formatTime((data.momentWindowEndsAt ?? 0) - 1, data.timeZone, 'zh-CN')} 前）。` : '')} />
}

function Detail({ teacherId, year, month }: { teacherId: string; year: number; month: number }) {
  const request = useCallback(() => getTeacherPerformance(teacherId, year, month), [teacherId, year, month])
  const { data, loading, error, retry } = useRemoteData(request)
  const [filter, setFilter] = useState<RecordFilter>('all')
  if (loading) return <Spin />
  if (error || !data) return <Alert type="error" message="加载教师表现失败" action={<Button onClick={retry}>重试</Button>} />
  const time = (value: number | null) => formatTime(value, data.timeZone, 'zh-CN')
  const columns: ColumnsType<PerformanceRecord> = [
    { title: '原定上课时间', dataIndex: 'classStartLocal', width: 160, render: value => value.replace('T', ' ') },
    { title: '预约 ID', dataIndex: 'appointmentId', width: 120 },
    { title: '出勤 / 取消', dataIndex: 'outcome', width: 110, render: value => <Tag>{outcomes[value]}</Tag> },
    { title: '迟到', width: 130, render: (_, row) => row.late == null
      ? (row.outcome === 'TEACHER_ABSENT' || row.outcome === 'TEACHER_CANCELLED' ? '不适用' : '未知')
      : row.late ? `晚到 ${row.lateSeconds} 秒` : '否' },
    { title: '总结', width: 170, render: (_, row) => row.summaryRequired
      ? `${row.summarySubmitted ? '已填写' : '未填写'} · ${timing[row.summaryTiming]}` : '无需填写' },
    { title: '首次有效提交', dataIndex: 'summaryFirstSubmittedAt', width: 175, render: time },
    { title: '提交截止时间', dataIndex: 'summaryDueAt', width: 175, render: time },
    { title: '取消原因', dataIndex: 'cancelReason', width: 180, render: value => value || '—' },
  ]
  return <Space direction="vertical" size="large" style={{ width: '100%' }}>
    <Typography.Title level={4}>{data.teacherName} · {year}-{String(month).padStart(2, '0')}</Typography.Title>
    <ScoreSummary data={data} />
    <Coverage data={data} />
    <Typography.Title level={5} style={{ margin: 0 }}>课堂</Typography.Title>
    <MetricCards data={data} keys={lessonKeys} />
    <Typography.Title level={5} style={{ margin: 0 }}>Moments</Typography.Title>
    <MetricCards data={data} keys={momentKeys} />
    <Select aria-label="筛选课程明细" value={filter} onChange={setFilter} style={{ minWidth: 220 }} options={[
      { value: 'all', label: '全部课程' }, { value: 'lateness', label: '迟到课程' }, { value: 'absence', label: '老师缺席' },
      { value: 'summaryCompletion', label: '总结未填写' }, { value: 'summaryTimeliness', label: '总结超时 / 逾期未提交' },
      { value: 'cancellation', label: '老师取消（含请假）' },
    ]} />
    <Table rowKey="appointmentId" columns={columns} dataSource={filterRecords(data.records, filter)} scroll={{ x: 1250 }}
      pagination={{ pageSize: 10, showSizeChanger: false }} locale={{ emptyText: '暂无符合条件的课程' }} />
    <Typography.Text type="secondary">更新于 {time(data.asOf)}（{data.timeZone}）</Typography.Text>
  </Space>
}

export default function TeacherPerformancePage() {
  const [month, setMonth] = useState(() => dayjs().startOf('month'))
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<string | null>(null)
  const year = month.year(), monthNumber = month.month() + 1
  const request = useCallback(() => getTeacherPerformances(year, monthNumber), [year, monthNumber])
  const { data, loading, error, retry } = useRemoteData(request)
  const rows = useMemo(() => filterTeachers(data ?? [], query), [data, query])
  const columns: ColumnsType<TeacherPerformance> = [
    { title: '老师 ID', dataIndex: 'teacherId', width: 100, fixed: 'left' },
    { title: '老师姓名', dataIndex: 'teacherName', width: 160, fixed: 'left', render: (_, row) => <Button type="link" onClick={() => setSelected(row.teacherId)}>{row.teacherName}</Button> },
    { title: '总分', key: 'score', width: 90, sorter: (a, b, order) => compareScores(a, b, order),
      render: (_, row) => <ScoreTag score={row.score?.value} /> },
    { title: '奖金', key: 'bonus', width: 120, render: (_, row) => <BonusTag report={row} /> },
    { title: '老师时区', dataIndex: 'timeZone', width: 130 },
    ...[...momentKeys, ...lessonKeys].map(key => ({ title: labels[key], key, width: 160,
      sorter: (a: TeacherPerformance, b: TeacherPerformance, order?: 'ascend' | 'descend' | null) => compareRates(a[key], b[key], order),
      render: (_: unknown, row: TeacherPerformance) => <Space direction="vertical" size={0}>
        <Typography.Text>{formatRate(row[key], 'zh-CN')}</Typography.Text>
        <Typography.Text type="secondary">{row[key].numerator} / {row[key].denominator}</Typography.Text>
      </Space> })),
    { title: '操作', key: 'action', width: 100, render: (_, row) => <Button onClick={() => setSelected(row.teacherId)}>详情</Button> },
  ]
  return <Space direction="vertical" size="large" style={{ display: 'flex', padding: 24 }}>
    <div><Typography.Title level={2}>教师表现</Typography.Title><Typography.Text type="secondary">查看绩效总分、各项指标率及课程明细。总分决定老师当月奖金档位。</Typography.Text></div>
    <Space wrap>
      <DatePicker picker="month" allowClear={false} aria-label="统计月份" value={month} onChange={value => { if (value) { setMonth(value); setSelected(null) } }} />
      <Input.Search allowClear aria-label="搜索老师" placeholder="老师姓名或 ID" value={query} onChange={event => setQuery(event.target.value)} style={{ width: 250 }} />
      <Button onClick={retry} loading={loading}>刷新</Button>
    </Space>
    <Alert type="info" message="各老师按个人时区统计；原定上课时间尚未到的课程暂不纳入。— 表示没有可统计数据，比例下方为次数 / 统计基数。标「预估」的老师还有数据未定，分数和奖金可能变化。" />
    {error ? <Alert type="error" showIcon message="加载教师表现失败" action={<Button onClick={retry}>重试</Button>} />
      : <Table rowKey="teacherId" columns={columns} dataSource={rows} loading={loading} scroll={{ x: 1960 }}
        pagination={{ pageSize: 20, showSizeChanger: false }} locale={{ emptyText: '暂无符合条件的老师' }} />}
    <Card title="统计口径" size="small"><Typography.Paragraph><strong>总分：</strong>{scoreRule}</Typography.Paragraph>{metricKeys.map(key => <Typography.Paragraph key={key}><strong>{labels[key]}：</strong>{help[key]}</Typography.Paragraph>)}</Card>
    <Drawer title="教师表现详情" width="min(1300px, 95vw)" open={selected !== null} onClose={() => setSelected(null)} destroyOnClose>
      {selected && <Detail key={`${selected}-${year}-${monthNumber}`} teacherId={selected} year={year} month={monthNumber} />}
    </Drawer>
  </Space>
}
